import React from 'react';
import { X, Plus, ShoppingCart, ShoppingBag, CheckCircle, Truck, XCircle, LogOut, MessageCircle, Clock, DollarSign, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Exit condition types with descriptions
const EXIT_CONDITIONS = [
  { 
    value: 'order_placed', 
    label: 'Order Placed', 
    icon: ShoppingCart,
    description: 'Exit when customer places any order',
    color: 'text-green-400'
  },
  { 
    value: 'checkout_started', 
    label: 'Checkout Started', 
    icon: ShoppingBag,
    description: 'Exit when customer starts a new checkout (Shopify only)',
    color: 'text-amber-400'
  },
  { 
    value: 'checkout_recovered', 
    label: 'Checkout Recovered', 
    icon: CheckCircle,
    description: 'Exit when abandoned checkout is completed',
    color: 'text-blue-400'
  },
  { 
    value: 'order_shipped', 
    label: 'Order Shipped', 
    icon: Truck,
    description: 'Exit when order is shipped',
    color: 'text-purple-400'
  },
  { 
    value: 'order_delivered', 
    label: 'Order Delivered', 
    icon: Truck,
    description: 'Exit when order is delivered',
    color: 'text-green-400'
  },
  { 
    value: 'order_cancelled', 
    label: 'Order Cancelled', 
    icon: XCircle,
    description: 'Exit when order is cancelled',
    color: 'text-red-400'
  },
  { 
    value: 'unsubscribed', 
    label: 'Customer Unsubscribed', 
    icon: LogOut,
    description: 'Exit when customer opts out of marketing',
    color: 'text-orange-400'
  },
  { 
    value: 'replied', 
    label: 'Customer Replied', 
    icon: MessageCircle,
    description: 'Exit when customer sends a reply',
    color: 'text-blue-400'
  },
  { 
    value: 'credits_used', 
    label: 'Credits Used', 
    icon: DollarSign,
    description: 'Exit when store credits are redeemed',
    color: 'text-amber-400'
  },
  { 
    value: 'custom', 
    label: 'Custom Condition', 
    icon: AlertTriangle,
    description: 'Define a custom field-based exit condition',
    color: 'text-gray-400'
  },
];

// Custom condition operators
const OPERATORS = [
  { value: '==', label: 'Equals' },
  { value: '!=', label: 'Not Equals' },
  { value: '>', label: 'Greater Than' },
  { value: '<', label: 'Less Than' },
  { value: '>=', label: 'Greater or Equal' },
  { value: '<=', label: 'Less or Equal' },
];

// Custom condition fields
const CUSTOM_FIELDS = [
  { value: 'order.total', label: 'Order Total' },
  { value: 'order.status', label: 'Order Status' },
  { value: 'customer.orders_count', label: 'Customer Orders Count' },
  { value: 'customer.total_spent', label: 'Customer Total Spent' },
  { value: 'checkout.status', label: 'Checkout Status' },
];

export function ExitConditionsConfig({ exitConditions = [], onChange }) {
  // Add a new exit condition
  const addCondition = () => {
    onChange([...exitConditions, { type: 'order_placed' }]);
  };

  // Remove an exit condition
  const removeCondition = (index) => {
    const updated = exitConditions.filter((_, i) => i !== index);
    onChange(updated);
  };

  // Update a condition
  const updateCondition = (index, updates) => {
    const updated = [...exitConditions];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const getConditionConfig = (type) => {
    return EXIT_CONDITIONS.find(c => c.value === type) || EXIT_CONDITIONS[0];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-wt-text-primary font-medium">Exit Conditions</Label>
          <p className="text-xs text-wt-text-muted mt-1">
            Stop the journey when any of these conditions are met
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
          <div className="text-xs text-amber-400">
            <p className="font-medium">Exit conditions are checked before each step</p>
            <p className="mt-1 text-amber-400/80">
              If any condition is met, the customer will exit the journey immediately and no further messages will be sent.
            </p>
          </div>
        </div>
      </div>

      {/* Current Conditions */}
      {exitConditions.length > 0 ? (
        <div className="space-y-3">
          {exitConditions.map((condition, index) => {
            const config = getConditionConfig(condition.type);
            const Icon = config.icon;

            return (
              <div 
                key={index} 
                className="p-3 bg-wt-paper rounded-lg border border-wt-border"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${config.color}`} />
                    <span className="text-sm font-medium text-wt-text-primary">
                      Exit Condition {index + 1}
                    </span>
                  </div>
                  <button
                    onClick={() => removeCondition(index)}
                    className="p-1 text-wt-text-muted hover:text-red-400 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Condition Type Selector */}
                <Select
                  value={condition.type}
                  onValueChange={(v) => updateCondition(index, { type: v })}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border max-h-64">
                    {EXIT_CONDITIONS.map(cond => (
                      <SelectItem 
                        key={cond.value} 
                        value={cond.value}
                        className="text-wt-text-primary"
                      >
                        <div className="flex items-center gap-2">
                          <cond.icon className={`h-4 w-4 ${cond.color}`} />
                          <div>
                            <span>{cond.label}</span>
                            <span className="text-xs text-wt-text-muted ml-2">
                              - {cond.description}
                            </span>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Custom Condition Fields */}
                {condition.type === 'custom' && (
                  <div className="mt-3 space-y-3 p-3 bg-wt-bg rounded-lg">
                    <div>
                      <Label className="text-xs text-wt-text-muted">Field</Label>
                      <Select
                        value={condition.field || ''}
                        onValueChange={(v) => updateCondition(index, { field: v })}
                      >
                        <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary mt-1">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent className="bg-wt-surface border-wt-border">
                          {CUSTOM_FIELDS.map(field => (
                            <SelectItem 
                              key={field.value} 
                              value={field.value}
                              className="text-wt-text-primary"
                            >
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-wt-text-muted">Operator</Label>
                        <Select
                          value={condition.operator || '=='}
                          onValueChange={(v) => updateCondition(index, { operator: v })}
                        >
                          <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-wt-surface border-wt-border">
                            {OPERATORS.map(op => (
                              <SelectItem 
                                key={op.value} 
                                value={op.value}
                                className="text-wt-text-primary"
                              >
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs text-wt-text-muted">Value</Label>
                        <Input
                          value={condition.value || ''}
                          onChange={(e) => updateCondition(index, { value: e.target.value })}
                          placeholder="Enter value"
                          className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Description for non-custom conditions */}
                {condition.type !== 'custom' && (
                  <p className="text-xs text-wt-text-muted mt-2">
                    {config.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-4 border-2 border-dashed border-wt-border rounded-lg text-center">
          <p className="text-sm text-wt-text-muted mb-3">
            No exit conditions configured
          </p>
          <p className="text-xs text-wt-text-muted">
            Add exit conditions to automatically stop the journey when specific events occur
          </p>
        </div>
      )}

      {/* Add Condition Button */}
      <Button
        variant="outline"
        onClick={addCondition}
        className="w-full border-dashed border-wt-border text-wt-text-muted hover:border-wt-gold hover:text-wt-gold"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Exit Condition
      </Button>

      {/* Recommended Exit Conditions */}
      {exitConditions.length === 0 && (
        <div className="pt-4 border-t border-wt-border">
          <Label className="text-xs text-wt-text-muted font-medium">RECOMMENDED</Label>
          <div className="mt-2 space-y-2">
            <button
              onClick={() => onChange([{ type: 'order_placed' }])}
              className="w-full flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/30 text-left hover:bg-green-500/20 transition-colors"
            >
              <ShoppingCart className="h-4 w-4 text-green-400" />
              <div>
                <p className="text-sm text-green-400 font-medium">Order Placed</p>
                <p className="text-xs text-wt-text-muted">Best for abandoned cart recovery</p>
              </div>
            </button>

            <button
              onClick={() => onChange([{ type: 'unsubscribed' }])}
              className="w-full flex items-center gap-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-left hover:bg-orange-500/20 transition-colors"
            >
              <LogOut className="h-4 w-4 text-orange-400" />
              <div>
                <p className="text-sm text-orange-400 font-medium">Customer Unsubscribed</p>
                <p className="text-xs text-wt-text-muted">Respect marketing preferences</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExitConditionsConfig;
